import { defineAppHotkeys } from '@tanstack/react-hotkeys';

/**
 * Where a hotkey fires. `global` is always active; the others are active
 * while a `<HotkeysProvider scope=…>` for them is mounted. Overlays mount
 * an exclusive scope, so only global keys reach past them.
 */
export const HOTKEY_SCOPES = {
  global: 'global',
  dashboard: 'dashboard',
  semanticCenter: 'semantic-center',
  semanticEditor: 'semantic-editor',
  /** The open step editor; its ⌘↵ (apply) wins over the page's (validate). */
  semanticEditorPanel: 'semantic-editor-panel',
  overlay: 'overlay'
} as const;

/**
 * Every app hotkey, by id. Bind with `useHotkey(APP_HOTKEYS[id], …)`; the
 * shortcuts sheet lists each one that has a `meta.group`, in this order.
 * Keys a widget handles itself (palette results, the Assistant composer)
 * are documented in `keyboard-shortcuts.ts`, not here.
 */
export const APP_HOTKEYS = defineAppHotkeys({
  'search.open': {
    hotkey: 'Mod+K',
    meta: { name: 'Search or ask anything', group: 'General' }
  },
  'assistant.toggle': {
    hotkey: 'Mod+J',
    meta: {
      name: 'Open the Assistant, or collapse it from its composer',
      group: 'General'
    }
  },
  'settings.open': { hotkey: 'Mod+,', meta: { name: 'Open Settings', group: 'General' } },
  'help.open': { hotkey: 'Mod+/', meta: { name: 'Open Help', group: 'General' } },
  'shortcuts.open': {
    hotkey: '?',
    meta: { name: 'Show keyboard shortcuts', group: 'General' }
  },

  'semanticCenter.search': {
    hotkey: '/',
    scope: HOTKEY_SCOPES.semanticCenter,
    meta: { name: 'Search sources and datasets', group: 'Semantic Center' }
  },

  'editor.selectPrevious': {
    hotkey: 'ArrowUp',
    scope: HOTKEY_SCOPES.semanticEditor,
    meta: { name: 'Select the previous card', group: 'Semantic Editor' }
  },
  'editor.selectNext': {
    hotkey: 'ArrowDown',
    scope: HOTKEY_SCOPES.semanticEditor,
    meta: { name: 'Select the next card', group: 'Semantic Editor' }
  },
  'editor.moveUp': {
    hotkey: 'Alt+ArrowUp',
    scope: HOTKEY_SCOPES.semanticEditor,
    meta: { name: 'Move the selected step up', group: 'Semantic Editor' }
  },
  'editor.moveDown': {
    hotkey: 'Alt+ArrowDown',
    scope: HOTKEY_SCOPES.semanticEditor,
    meta: { name: 'Move the selected step down', group: 'Semantic Editor' }
  },
  'editor.addStep': {
    hotkey: 'A',
    scope: HOTKEY_SCOPES.semanticEditor,
    meta: { name: 'Add a step below the selected card', group: 'Semantic Editor' }
  },
  // The first-step card's keys. The chooser menu takes the same letters
  // itself, so the sheet lists them once, as a widget row.
  'editor.firstStep.filter': { hotkey: 'F', scope: HOTKEY_SCOPES.semanticEditor },
  'editor.firstStep.enrich': { hotkey: 'E', scope: HOTKEY_SCOPES.semanticEditor },
  'editor.firstStep.aggregate': { hotkey: 'G', scope: HOTKEY_SCOPES.semanticEditor },
  'editor.firstStep.map': { hotkey: 'M', scope: HOTKEY_SCOPES.semanticEditor },
  'editor.firstStep.sql': { hotkey: 'S', scope: HOTKEY_SCOPES.semanticEditor },
  'editor.duplicate': {
    hotkey: 'Mod+D',
    scope: HOTKEY_SCOPES.semanticEditor,
    meta: { name: 'Duplicate the selected step', group: 'Semantic Editor' }
  },
  'editor.remove': {
    hotkey: ['Backspace', 'Backspace'],
    scope: HOTKEY_SCOPES.semanticEditor,
    options: { timeout: 1500 },
    meta: { name: 'Remove the selected step (press twice)', group: 'Semantic Editor' }
  },
  'editor.preview': {
    hotkey: 'Space',
    scope: HOTKEY_SCOPES.semanticEditor,
    meta: { name: 'Preview the data at the selected card', group: 'Semantic Editor' }
  },
  'editor.rename': {
    hotkey: 'R',
    scope: HOTKEY_SCOPES.semanticEditor,
    meta: { name: 'Rename the dataset', group: 'Semantic Editor' }
  },
  'editor.recenter': {
    hotkey: '0',
    scope: HOTKEY_SCOPES.semanticEditor,
    meta: { name: 'Recenter the spine', group: 'Semantic Editor' }
  },
  'editor.togglePanel': {
    hotkey: ']',
    scope: HOTKEY_SCOPES.semanticEditor,
    meta: { name: 'Show or hide the side panel', group: 'Semantic Editor' }
  },
  'editor.validate': {
    hotkey: 'Mod+Enter',
    scope: HOTKEY_SCOPES.semanticEditor,
    meta: { name: 'Validate, or apply the open step', group: 'Semantic Editor' }
  },
  // Same keys as validate, in the panel's scope, which wins while mounted.
  'editor.apply': { hotkey: 'Mod+Enter', scope: HOTKEY_SCOPES.semanticEditorPanel },
  'editor.publish': {
    hotkey: 'Mod+Shift+Enter',
    scope: HOTKEY_SCOPES.semanticEditor,
    meta: { name: 'Publish', group: 'Semantic Editor' }
  },
  'editor.askAssistant': {
    hotkey: 'Mod+I',
    scope: HOTKEY_SCOPES.semanticEditor,
    meta: { name: 'Ask the Assistant about this pipeline', group: 'Semantic Editor' }
  },
  'editor.escape': {
    hotkey: 'Escape',
    scope: HOTKEY_SCOPES.semanticEditor,
    // Esc in a field belongs to the field. Not prevented here, so the handler
    // can tell when a menu or dialog already used this press.
    options: { ignoreInputs: true, preventDefault: false },
    meta: {
      name: 'Close the preview, then Validate and publish, then the selection',
      group: 'Semantic Editor'
    }
  },

  'dashboard.closeDetail': {
    hotkey: 'Escape',
    scope: HOTKEY_SCOPES.dashboard,
    meta: { name: 'Close the security core detail', group: 'Dashboard' }
  }
});

declare module '@tanstack/hotkeys' {
  interface Register {
    appHotkeys: typeof APP_HOTKEYS;
  }
}
